import { roundedRect } from '@bn-games/render-helpers';
import type { DominoPlayer } from '../game/types.js';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../utils/constants.js';
import type { ChainRegion } from './BoardRenderer.js';
import type { RenderPalette } from './palette.js';

/**
 * Logical identifiers for player actions surfaced as focusable controls. The
 * action bar only renders Muggins; Draw is rendered as the boneyard tile-stack
 * on the table (see `BoneyardStack.ts`). The shared id keeps the keyboard
 * focus chain agnostic to which surface owns the action.
 */
export type ActionButtonId = 'draw' | 'muggins';

export interface ActionButton {
  readonly id: 'muggins';
  readonly label: string;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly disabled: boolean;
  /** Pulsing emphasis (used to draw the eye to a Muggins claim window). */
  readonly highlight: boolean;
}

export interface ActionBarState {
  readonly mugginsVisible: boolean;
  readonly mugginsEnabled: boolean;
  /** 0..1 — fraction of the Muggins window remaining; drives the countdown ring. */
  readonly mugginsRemaining: number;
}

const BUTTON_W = 130;
const BUTTON_H = 38;
const BUTTON_INSET = 16;
const BUTTON_FONT = "600 14px 'Segoe UI', system-ui, sans-serif";
const BUTTON_RADIUS = 8;

/**
 * Compute the Muggins button rect anchored to the bottom-right corner of the
 * chain region. `bottomReserved` slides the button up so it stacks above the
 * boneyard pile (which sits in the same corner).
 */
export function layoutActionBar(
  state: ActionBarState,
  mugginsLabel: string,
  chainRegion: ChainRegion,
  bottomReserved = 0,
): ActionButton[] {
  if (!state.mugginsVisible) return [];
  const baseY =
    chainRegion.y + chainRegion.height - BUTTON_INSET - BUTTON_H - bottomReserved;
  const x = chainRegion.x + chainRegion.width - BUTTON_INSET - BUTTON_W;
  return [
    {
      id: 'muggins',
      label: mugginsLabel,
      x,
      y: baseY,
      w: BUTTON_W,
      h: BUTTON_H,
      disabled: !state.mugginsEnabled,
      highlight: state.mugginsEnabled,
    },
  ];
}

export function drawActionBar(
  ctx: CanvasRenderingContext2D,
  buttons: readonly ActionButton[],
  palette: RenderPalette,
  mugginsRemaining: number,
): void {
  ctx.save();
  for (const b of buttons) {
    ctx.save();
    if (b.disabled) ctx.globalAlpha = 0.45;

    // Soft shadow.
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 2;
    roundedRect(ctx, b.x, b.y, b.w, b.h, BUTTON_RADIUS);
    ctx.fillStyle = b.highlight ? palette.validArmGlow : palette.bannerBg;
    ctx.fill();
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;

    // Border.
    ctx.strokeStyle = palette.bannerBorder;
    ctx.lineWidth = 1.5;
    roundedRect(ctx, b.x + 0.5, b.y + 0.5, b.w - 1, b.h - 1, BUTTON_RADIUS);
    ctx.stroke();

    // Label.
    ctx.fillStyle = palette.bannerTextColor;
    ctx.font = BUTTON_FONT;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(b.label, b.x + b.w / 2, b.y + b.h / 2);

    // Muggins countdown ring along the bottom edge of the button.
    if (b.id === 'muggins' && b.highlight && mugginsRemaining > 0) {
      const trackY = b.y + b.h - 4;
      const trackX = b.x + 6;
      const trackW = b.w - 12;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.25)';
      ctx.fillRect(trackX, trackY, trackW, 2);
      ctx.fillStyle = palette.selectedRing;
      ctx.fillRect(trackX, trackY, trackW * Math.max(0, Math.min(1, mugginsRemaining)), 2);
    }
    ctx.restore();
  }
  ctx.restore();
}

/** Returns 'muggins' when (x, y) hits the enabled Muggins button, else null. */
export function hitTestActionBar(
  buttons: readonly ActionButton[],
  x: number,
  y: number,
): 'muggins' | null {
  for (const b of buttons) {
    if (b.disabled) continue;
    if (x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h) {
      return b.id;
    }
  }
  return null;
}

// -------------------- Turn banner --------------------

export interface TurnBannerView {
  readonly text: string;
}

const BANNER_FONT = "600 13px 'Segoe UI', system-ui, sans-serif";
const BANNER_H = 28;
const BANNER_RADIUS = 14;
const BANNER_TOP = 6;
const BANNER_PADDING = 16;

/**
 * Pill-style turn indicator at top-center — only carries whose turn it is
 * (or the current hand/match state). Per-seat score lives in the player
 * info panels in each hand zone, not here.
 */
export function drawTurnBanner(
  ctx: CanvasRenderingContext2D,
  view: TurnBannerView,
  palette: RenderPalette,
): void {
  if (view.text.length === 0) return;
  ctx.save();
  ctx.font = BANNER_FONT;
  const textWidth = ctx.measureText(view.text).width;
  const w = textWidth + BANNER_PADDING * 2;
  const x = (CANVAS_WIDTH - w) / 2;
  const y = BANNER_TOP;

  ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  roundedRect(ctx, x, y, w, BANNER_H, BANNER_RADIUS);
  ctx.fillStyle = palette.bannerBg;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = palette.bannerBorder;
  ctx.lineWidth = 1.5;
  roundedRect(ctx, x + 0.5, y + 0.5, w - 1, BANNER_H - 1, BANNER_RADIUS);
  ctx.stroke();

  ctx.fillStyle = palette.bannerTextColor;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(view.text, CANVAS_WIDTH / 2, y + BANNER_H / 2);
  ctx.restore();
}

// -------------------- Hot-seat curtain --------------------

export interface CurtainView {
  readonly title: string;
  readonly subtitle: string;
  readonly buttonLabel: string;
}

export interface CurtainButton {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

const CURTAIN_BTN_W = 220;
const CURTAIN_BTN_H = 48;
const CURTAIN_TITLE_FONT = "700 28px 'Segoe UI', system-ui, sans-serif";
const CURTAIN_SUB_FONT = "500 16px 'Segoe UI', system-ui, sans-serif";
const CURTAIN_BTN_FONT = "700 17px 'Segoe UI', system-ui, sans-serif";

export function drawCurtain(
  ctx: CanvasRenderingContext2D,
  view: CurtainView,
  palette: RenderPalette,
): CurtainButton {
  ctx.save();
  // Full-canvas dim overlay so neither hand can be read through it.
  ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
  ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

  ctx.fillStyle = palette.textPrimary;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = CURTAIN_TITLE_FONT;
  ctx.fillText(view.title, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 40);

  ctx.font = CURTAIN_SUB_FONT;
  ctx.fillStyle = palette.textSecondary;
  ctx.fillText(view.subtitle, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2 - 4);

  const btnX = (CANVAS_WIDTH - CURTAIN_BTN_W) / 2;
  const btnY = CANVAS_HEIGHT / 2 + 30;
  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
  ctx.shadowBlur = 10;
  ctx.shadowOffsetY = 3;
  roundedRect(ctx, btnX, btnY, CURTAIN_BTN_W, CURTAIN_BTN_H, 10);
  ctx.fillStyle = palette.bannerBg;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
  ctx.strokeStyle = palette.bannerBorder;
  ctx.lineWidth = 2;
  roundedRect(ctx, btnX + 0.5, btnY + 0.5, CURTAIN_BTN_W - 1, CURTAIN_BTN_H - 1, 10);
  ctx.stroke();

  ctx.fillStyle = palette.bannerTextColor;
  ctx.font = CURTAIN_BTN_FONT;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(view.buttonLabel, btnX + CURTAIN_BTN_W / 2, btnY + CURTAIN_BTN_H / 2);

  ctx.restore();
  return { x: btnX, y: btnY, w: CURTAIN_BTN_W, h: CURTAIN_BTN_H };
}

export function hitTestCurtain(btn: CurtainButton, x: number, y: number): boolean {
  return x >= btn.x && x <= btn.x + btn.w && y >= btn.y && y <= btn.y + btn.h;
}

// The hand/match-end result modal moved to a DOM modal in iter 3 — see
// `syncResultModal` in main.ts (backed by `openModal` from `@bn-games/ui-shell`).
// Canvas drawing for the result overlay was removed at the same time so the
// buttons could be focus-trapped and keyboard-reachable.

// Re-export for convenience in main.ts wiring.
export type { DominoPlayer };
